import { GitPullRequestIcon, PlayIcon, WarningIcon } from '@phosphor-icons/react';

import { Alert } from '@/ui/Alert';
import { Button } from '@/ui/button/Button';

import { BranchPair } from './BranchPair';

interface ReviewGateProps {
  base: string;
  branch: string;
  onStart: () => void;
  starting: boolean;
  error: string | null;
  blocker: string | null;
}

/**
 * The Review tab's empty state, shown until a review is started. Nothing else — no diff, commits
 * or description — renders behind it; the single "Start Review" action commits the agent's work and
 * drafts the PR copy, then reveals the rest.
 */
export function ReviewGate({ base, branch, onStart, starting, error, blocker }: ReviewGateProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 bg-surface-1 px-6 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-surface-2 text-fg-secondary">
        <GitPullRequestIcon size={24} />
      </div>
      <div className="flex flex-col items-center gap-1.5">
        <h2 className="text-sm font-semibold text-fg">Start a review</h2>
        <p className="max-w-sm text-xs leading-relaxed text-fg-tertiary">
          Commits the agent&apos;s latest work, then drafts a title and description with AI. The changes, commits and
          description open once it&apos;s ready.
        </p>
        <BranchPair base={base} head={branch} />
      </div>
      {blocker && (
        <Alert variant="warning" icon={<WarningIcon size={14} />} className="max-w-sm p-3 text-xs">
          {blocker}
        </Alert>
      )}
      <Button
        type="button"
        size={9}
        onClick={onStart}
        isLoading={starting}
        disabled={starting || blocker !== null}
        iconLeft={<PlayIcon size={15} weight="fill" />}
      >
        {starting ? 'Starting review…' : 'Start Review'}
      </Button>
      {error && (
        <Alert variant="danger" icon={<WarningIcon size={14} />} className="max-w-sm p-3 text-xs">
          {error}
        </Alert>
      )}
    </div>
  );
}
